import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import {
  getSignColor,
  formatCurrency,
  formatCompactCurrency,
  formatPercentPlain,
  formatPercentSigned,
  formatLongCapital,
  formatShortCapital,
  formatNetCapital,
  formatPercentColored,
  isValidNum,
} from "../../PerformanceDashboard/utility";
import { v2CardBg, v2CardBorder, v2LabelSx, v2MutedSx, v2PeriodTint, v2Accent } from "../v2Theme";

const PeriodChip = ({ label, value, isFactor, theme }) => {
  const num = Number(value);
  const showDash = isFactor || !isValidNum(value);
  const positive = num >= 0;
  const tint = showDash ? "transparent" : v2PeriodTint(theme, positive);
  const color = showDash
    ? theme.palette.mode === "dark"
      ? "#C8C8C8"
      : theme.palette.text.secondary
    : getSignColor(num, theme);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "center",
        px: 0.35,
        py: 0.4,
        borderRadius: 1,
        backgroundColor: tint,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.04em",
          ...v2LabelSx(theme),
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          fontSize: "0.88rem",
          color,
        }}
      >
        {showDash ? "-" : formatPercentColored(value)}
      </Typography>
    </Box>
  );
};

const FooterMetric = ({ label, value, color, theme }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontSize: "0.72rem",
        lineHeight: 1.15,
        fontWeight: 600,
        ...v2LabelSx(theme),
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        fontSize: "0.88rem",
        color: color || "text.primary",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const StrategyPerformanceCard = ({
  row,
  collapsed = false,
  onFundClick,
  onStrategyClick,
}) => {
  const theme = useTheme();
  // SMAs reuse CPFactorBW as a bucket but have real fund-level period RoR.
  const isFactor = row.strategyName === "CPFactorBW" && !row.isSMA;
  const plSign = Number(row.plPer) || Number(row.pL) || 0;
  const accent = getSignColor(plSign, theme);
  const isDark = theme.palette.mode === "dark";

  const handleCardClick = () => {
    if (isFactor) {
      onFundClick?.(row.fundName);
    } else {
      onStrategyClick?.(row.fundName, row.strategyName);
    }
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        height: "100%",
        minHeight: collapsed ? 210 : 270,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.36)" : v2CardBorder(theme)}`,
        borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.36)" : v2CardBorder(theme)}`,
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.36)" : v2CardBorder(theme)}`,
        borderLeft: `3px solid ${accent}`,
        backgroundColor: v2CardBg(theme),
        boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
        color: "text.primary",
        cursor: "pointer",
        overflow: "hidden",
        transition: "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderTopColor: isDark ? "rgba(255,255,255,0.5)" : alpha(v2Accent(theme), 0.45),
          borderRightColor: isDark ? "rgba(255,255,255,0.5)" : alpha(v2Accent(theme), 0.45),
          borderBottomColor: isDark ? "rgba(255,255,255,0.5)" : alpha(v2Accent(theme), 0.45),
          boxShadow: isDark
            ? "0 4px 16px rgba(0,0,0,0.35)"
            : "0 4px 12px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
          px: 1.5,
          pt: 1.15,
          pb: 0.75,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.primary",
              fontSize: "1.05rem",
            }}
            title={row.strategyName}
          >
            {row.strategyName}
          </Typography>
          {(row.fundDisplayName || row.fundName) && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.15,
                fontWeight: 600,
                fontSize: "0.78rem",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...v2MutedSx(theme),
              }}
              title={row.fundDisplayName || row.fundName}
            >
              {row.fundDisplayName || row.fundName}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.35,
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: "0.8rem",
              ...v2MutedSx(theme),
            }}
          >
            AUM {formatCompactCurrency(row.aum)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.35,
              color: getSignColor(row.plPer, theme),
            }}
          >
            {isValidNum(row.plPer) &&
              (Number(row.plPer) >= 0 ? (
                <ArrowDropUpRoundedIcon sx={{ fontSize: "1.75rem", ml: -0.5 }} />
              ) : (
                <ArrowDropDownRoundedIcon sx={{ fontSize: "1.75rem", ml: -0.5 }} />
              ))}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
                color: "inherit",
                lineHeight: 1.1,
                fontSize: { xs: "1.3rem", md: "1.45rem" },
              }}
            >
              {isValidNum(row.plPer) ? `${Math.abs(Number(row.plPer)).toFixed(2)}%` : "—"}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              letterSpacing: "0.04em",
              fontSize: "0.72rem",
              ...v2LabelSx(theme),
            }}
          >
            TODAY
          </Typography>
        </Box>
      </Box>

      {/* Today band */}
      <Box
        sx={{
          mx: 1.25,
          mb: 1,
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : alpha(theme.palette.text.primary, 0.03),
          display: "grid",
          gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.2fr)",
          columnGap: 0.75,
        }}
      >
        {[
          { label: "P(L)", value: formatCurrency(row.pL), color: getSignColor(row.pL, theme) },
          { label: "Long", value: formatCurrency(row.plLong), color: getSignColor(row.plLong, theme) },
          { label: "Short", value: formatCurrency(row.plShort), color: getSignColor(row.plShort, theme) },
          {
            label: "Long Perf",
            value: formatPercentColored(row.longPerformance),
            color: getSignColor(row.longPerformance, theme),
          },
          {
            label: "Short Perf",
            value: formatPercentColored(row.shortPerformance),
            color: getSignColor(row.shortPerformance, theme, true),
          },
        ].map((m) => (
          <Box key={m.label} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: "0.72rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
                ...v2LabelSx(theme),
              }}
            >
              {m.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: m.color,
                fontSize: "0.95rem",
              }}
            >
              {m.value || "—"}
            </Typography>
          </Box>
        ))}
      </Box>

      {!collapsed && (
        <Box
          sx={{
            mx: 1.25,
            mb: 1,
            px: 1.15,
            py: 0.85,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: v2CardBorder(theme),
            backgroundColor: isDark ? "rgba(0,0,0,0.2)" : alpha(theme.palette.text.primary, 0.02),
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            columnGap: 1,
            rowGap: 1,
          }}
        >
          <FooterMetric label="DA Long" value={formatPercentPlain(row.sizeLong)} theme={theme} />
          <FooterMetric label="DA Short" value={formatPercentSigned(row.sizeShort)} theme={theme} />
          <FooterMetric label="Net AUM %" value={formatPercentSigned(row.netAumPercent)} theme={theme} />
          <FooterMetric label="Long Cap" value={formatLongCapital(row.longCapital)} theme={theme} />
          <FooterMetric label="Short Cap" value={formatShortCapital(row.shortCapital)} theme={theme} />
          <FooterMetric label="Net Cap" value={formatNetCapital(row.netCapital)} theme={theme} />
        </Box>
      )}

      {/* Period strip */}
      <Box
        sx={{
          mt: "auto",
          display: "flex",
          gap: 0.35,
          px: 1,
          py: 0.65,
          borderTop: "1px solid",
          borderColor: v2CardBorder(theme),
          backgroundColor: isDark ? "rgba(0,0,0,0.25)" : alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <PeriodChip label="Yest" value={row.pLYest} isFactor={false} theme={theme} />
        <PeriodChip label="Today" value={row.plPer} isFactor={false} theme={theme} />
        <PeriodChip label="MTD" value={row.pLMonth} isFactor={isFactor} theme={theme} />
        <PeriodChip label="QTD" value={row.pLQuarter} isFactor={isFactor} theme={theme} />
        <PeriodChip label="YTD" value={row.pLYear} isFactor={isFactor} theme={theme} />
        <PeriodChip label="ITD" value={row.pLITD} isFactor={isFactor} theme={theme} />
      </Box>
    </Box>
  );
};

export default StrategyPerformanceCard;
